import { Component, OnInit, OnDestroy } from '@angular/core';
import { Order } from 'src/app/_models/order';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { OrderService } from 'src/app/_services/order.service';
import { User } from 'src/app/_models/user';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-order',
    templateUrl: './order.component.html',
    styleUrls: ['./order.component.css'],
    standalone: false
})
export class OrderComponent implements OnInit, OnDestroy {

    loading = true;
    title = 'View Order';
    titleKey = 'orders_list.view_order';
    order: Order;
    lineOffers: any[] = [];
    orderRef: string;
    user: User;
    totalOrder = 0;
    lang = 'fr';
    expandedLineOfferId: number | null = null;
    loadingLineOffer = false;
    lineOfferDetail: any = null;

    private subscriptions: Subscription[] = [];

    constructor(private activatedRoute: ActivatedRoute,
        private router: Router, private titleService: Title,
        private orderService: OrderService, private translateService: TranslateService) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.orderRef = this.activatedRoute.snapshot.paramMap.get('orderRef') ?? '';
        this.lang = this.translateService.currentLang;

        const subs0 = this.translateService.get(this.titleKey).subscribe(value => {
            this.title = value;
            this.titleService.setTitle(this.title);
        });

        this.subscriptions.push(subs0);

        if (this.orderRef == null) {
            this.router.navigate(['not-found']);
        }
    }

    ngOnInit() {
        const subs1 = this.orderService.loadOrderByRef(this.user.id, this.orderRef).subscribe(
            (order: Order) => {
                this.order = order;
                this.lineOffers = order.lineOffers;
                this.lineOffers.forEach((lineOffer: any) => this.totalOrder += this.calculateTotal(lineOffer));
            },
            () => this.router.navigate(['not-found']),
            () => this.loading = false
        );

        this.subscriptions.push(subs1);
    }

    calculateTotal(lineOffer: any): number {
        return lineOffer.qty * lineOffer.price;
    }

    lineOfferSubtotal(lineOffer: any): number {
        return lineOffer.qty * this.getDiscountedPrice(lineOffer);
    }

    getDiscountedPrice(lineOffer: any): number {
        if (lineOffer.discount && lineOffer.discount > 0) {
            return lineOffer.price - (lineOffer.price * lineOffer.discount / 100);
        }
        return lineOffer.price;
    }

    productPictureUrl(lineOffer: any): string {
        const picture = lineOffer.product?.picture || lineOffer.product?.defaultPicture;
        if (!picture) return '';
        return environment.image_cdn_url + '/' + picture;
    }

    getProductName(lineOffer: any): string {
        if (!lineOffer.product) return '';
        return this.lang === 'fr' && lineOffer.product.productNameFr
            ? lineOffer.product.productNameFr
            : lineOffer.product.productName;
    }

    getCategoryName(lineOffer: any): string {
        if (!lineOffer.product?.category) return '';
        return this.lang === 'fr' && lineOffer.product.category.nameFr
            ? lineOffer.product.category.nameFr
            : lineOffer.product.category.name;
    }

    getConditionDescription(lineOffer: any): string {
        if (!lineOffer.product?.productCondition) return '';
        return this.lang === 'fr' && lineOffer.product.productCondition.descriptionFr
            ? lineOffer.product.productCondition.descriptionFr
            : lineOffer.product.productCondition.description;
    }

    getMeasurementDescription(lineOffer: any): string {
        if (!lineOffer.product?.measurementUnit) return '';
        return this.lang === 'fr' && lineOffer.product.measurementUnit.descriptionFr
            ? lineOffer.product.measurementUnit.descriptionFr
            : lineOffer.product.measurementUnit.description;
    }

    getSellerName(lineOffer: any): string {
        if (!lineOffer.seller) return '';
        return lineOffer.seller.firstName + ' ' + lineOffer.seller.lastName;
    }

    deliveryAddressString(lineOffer: any): string {
        const addr = lineOffer.logisticInfo?.deliveryAddress;
        if (!addr) return '';
        return [addr.addressLine1, addr.addressLine2, addr.town, addr.country]
            .filter((v: string) => v)
            .join(', ');
    }

    getDeliveryStatus(lineOffer: any): string {
        const status = lineOffer.logisticInfo?.deliveryStatus || '';
        return status ? 'delivery.status_' + status.toLowerCase() : '';
    }

    getDeliveryStatusRaw(lineOffer: any): string {
        return lineOffer.logisticInfo?.deliveryStatus || '';
    }

    getPaymentMethodLabel(method: string): string {
        switch (method) {
            case 'FLUTTERWAVE': return 'Flutterwave';
            case 'CompanyMobileMoney': return 'Mobile Money';
            case 'CashOnDelivery': return 'Cash On Delivery';
            default: return method || '';
        }
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
        return date.toLocaleDateString(this.lang === 'fr' ? 'fr-FR' : 'en-US', options);
    }

    formatCurrency(amount: number, currency?: string): string {
        if (amount == null) return '';
        return new Intl.NumberFormat(this.lang === 'fr' ? 'fr-FR' : 'en-US').format(amount) + ' ' + (currency || 'XAF');
    }

    getStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'PENDING': return 'badge bg-warning text-dark';
            case 'CONFIRMED': return 'badge bg-primary';
            case 'SUCCESS': return 'badge bg-success';
            case 'SHIPPED': return 'badge bg-purple';
            case 'DELIVERED': return 'badge bg-success';
            case 'CANCELLED': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    }

    toggleLineOffer(lineOffer: any): void {
        if (this.expandedLineOfferId === lineOffer.id) {
            this.expandedLineOfferId = null;
            this.lineOfferDetail = null;
            return;
        }
        this.expandedLineOfferId = lineOffer.id;
        this.loadingLineOffer = true;
        this.lineOfferDetail = null;
        this.orderService.loadLineOffer(lineOffer.id).subscribe({
            next: (res: any) => {
                this.lineOfferDetail = res.data;
                this.loadingLineOffer = false;
            },
            error: () => { this.loadingLineOffer = false; }
        });
    }

    getDeliverySteps(): { key: string; labelKey: string }[] {
        return [
            { key: 'PENDING',       labelKey: 'tracking.step_processing' },
            { key: 'IN_CHARGE',     labelKey: 'tracking.step_handed_over' },
            { key: 'IN_TRANSIT',    labelKey: 'tracking.step_in_transit' },
            { key: 'IN_DELIVERING', labelKey: 'tracking.step_in_delivering' },
            { key: 'DELIVERED',     labelKey: 'tracking.step_delivered' }
        ];
    }

    getCurrentDeliveryStepIndex(lineOffer: any): number {
        const status = (lineOffer.logisticInfo?.deliveryStatus || '').toUpperCase();
        const steps = this.getDeliverySteps().map(s => s.key);
        const idx = steps.indexOf(status);
        return idx >= 0 ? idx : 0;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

}
